/**
 * RequireAuth — guard для приватних роутів (Sprint 8b).
 *
 * Якщо токена немає → редирект на /login зі збереженням `from` у state,
 * щоб після успішного логіну повернути користувача туди, куди він йшов.
 *
 * NB: ми НЕ перевіряємо isExpired() тут. Протермінований access — це
 * нормальний стан, який автоматично оновиться через refresh-flow у
 * api/client.ts при першому 401. Якщо refresh теж невалідний — client
 * сам викличе logout() і фуллпейдж redirect на /login.
 *
 * Тобто guard перевіряє лише "був колись логін взагалі", не "токен
 * валідний прямо зараз".
 */
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { levelOf, useAuthStore } from "@/store/auth";
import { landingForRoles, roleUa } from "@/lib/nav";

/** Екран «немає прав» — із виходом, а не глухий кут. */
function NoAccess({
  requireRoles,
  myRoles,
  takenAway = false,
}: {
  requireRoles: string[];
  myRoles: string[];
  /** Роль підходить, але розділ забрано в «Правах» — інша причина, інший текст. */
  takenAway?: boolean;
}) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const home = landingForRoles(myRoles);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-aurora-cool">
      <div className="max-w-md w-full text-center rounded-[20px] bg-surface-strong backdrop-blur-glass border border-glass-border shadow-glassXl p-8">
        <div className="text-5xl mb-3">🔒</div>
        <h2 className="text-xl font-extrabold text-aurora-ink m-0 mb-2">
          {takenAway ? "Доступ до цього розділу забрано" : "Ця сторінка не для вашої ролі"}
        </h2>
        {takenAway ? (
          <p className="text-aurora-muted text-sm mb-6">
            Роль у вас підходить, але в «Правах» цей розділ для вас вимкнено.
            Якщо він потрібен — зверніться до адміністратора.
          </p>
        ) : (<>
        <p className="text-aurora-muted text-sm mb-1">
          Потрібна роль: <b>{requireRoles.map(roleUa).join(" або ")}</b>.
        </p>
        <p className="text-aurora-muted text-sm mb-6">
          {myRoles.length > 0 ? (
            <>Ваша роль: <b>{myRoles.map(roleUa).join(", ")}</b>.</>
          ) : (
            <>У вашому обліковому записі немає жодної ролі — зверніться до майстра.</>
          )}
        </p>
        </>)}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => navigate(home, { replace: true })}
            className="w-full h-12 rounded-[12px] bg-aurora-primary text-white font-bold shadow-auroraBtn active:scale-[0.99]"
          >
            На мій робочий екран
          </button>
          {/* «Назад» навмисно немає: при прямому переході за старим лінком
              navigate(-1) вело б знову в цю саму заборонену сторінку (цикл
              403). «На мій екран» + «Вийти» покривають вихід повністю. */}
          <button
            onClick={() => logout({ leaveDevice: true })}
            className="w-full h-11 rounded-[12px] text-aurora-muted text-sm font-semibold"
          >
            Вийти з системи
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
  /** Опційно: якщо вказано — гарантуємо що користувач має одну з цих ролей. */
  requireRoles?: string[];
  /** Ключ розділу в «Правах» (як `sect` у nav.ts). Видано «перегляд» чи
   *  «повний» — пускаємо й без ролі; «немає» — не пускаємо й за роллю.
   *
   *  Доти guard дивився лише на ролі: бухгалтерці видали «Люди», меню пункт
   *  показало, а сторінка відповіла «не для вашої ролі» (скарга 26.09.2026). */
  sect?: string;
}

export function RequireAuth({ children, requireRoles, sect }: Props) {
  const location = useLocation();
  const isAuth = useAuthStore((s) => s.isAuthenticated());
  const claims = useAuthStore((s) => s.claims);
  const access = useAuthStore((s) => s.access);

  if (!isAuth) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requireRoles && requireRoles.length > 0) {
    const userRoles = new Set(claims?.roles ?? []);
    const level = sect ? levelOf(access, sect) : undefined;
    const allowed =
      level === "none"
        ? false
        : requireRoles.some((r) => userRoles.has(r)) || level === "view" || level === "full";
    if (!allowed) {
      // 403-аналог на клієнті: показуємо сторінку, не редирект (у /login він
      // уже залогінений — форма виглядала б дивно). ОБОВʼЯЗКОВО з виходом:
      // раніше це був білий екран без меню й кнопок — глухий кут, з якого
      // людина в цеху виходила лише перезавантаженням.
      return (
        <NoAccess
          requireRoles={requireRoles}
          myRoles={claims?.roles ?? []}
          takenAway={level === "none" && requireRoles.some((r) => userRoles.has(r))}
        />
      );
    }
  }

  return <>{children}</>;
}
